/** The versioned Tests feed: apply only complete snapshots, including their proof watermark. */
import type { TestRecordSnapshot } from '../../../api/src/ai/test-record.js'
import type { ProofDecision } from '../../../api/src/ai/test-record-proof.js'
import { API_BASE_URL } from '../config.js'

export interface ProjectTestRecordPayload {
  snapshot: TestRecordSnapshot
  proof: ProofDecision
}
export interface ProjectTestRecordOptions {
  projectId: string
  getAuthToken: () => string | null | undefined
  onSnapshot: (payload: ProjectTestRecordPayload) => void
  onUnavailable: (message: string) => void
  baseUrl?: string
  fetchImpl?: typeof fetch
  retryDelayMs?: number
}
export function watchProjectTestRecords(options: ProjectTestRecordOptions): () => void {
  const controller = new AbortController(),
    doFetch = options.fetchImpl || fetch,
    base = options.baseUrl ?? API_BASE_URL
  let stopped = false,
    lastId: string | null = null
  const loop = async (): Promise<void> => {
    while (!stopped) {
      try {
        const token = options.getAuthToken(),
          headers: Record<string, string> = { Accept: 'text/event-stream' }
        if (token) headers.Authorization = `Bearer ${token}`
        if (lastId) headers['Last-Event-ID'] = lastId
        const response = await doFetch(`${base}/projects/${options.projectId}/tests/runs?v=2`, {
          headers,
          credentials: 'include',
          signal: controller.signal,
        })
        if (response.status === 401 || response.status === 403 || response.status === 404) {
          options.onUnavailable('Access to test records is unavailable.')
          return
        }
        if (!response.ok || !response.body) throw new Error('Test feed unavailable')
        const reader = response.body.getReader(),
          decoder = new TextDecoder()
        // The writer admits 256 MiB of pinned JSONL; normalized snapshots also duplicate
        // start/finish metadata. Keep the bounded transport large enough to replay that store.
        let buffer = '',
          assembly: { id: string; count: number; chunks: string[]; bytes: number } | null = null
        for (;;) {
          const chunk = await reader.read()
          if (chunk.done) break
          buffer += decoder.decode(chunk.value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() || ''
          if (buffer.length > 1_000_000) throw new Error('Invalid test feed frame')
          for (const line of lines) {
            if (!line.startsWith('data: ')) continue
            const frame = JSON.parse(line.slice(6)) as {
              type: string
              message?: string
              v?: string
              id?: string
              index?: number
              count?: number
              chunk?: string
            }
            if (frame.type === 'test-record-unavailable') {
              options.onUnavailable(frame.message || 'Test records unavailable')
              continue
            }
            if (
              frame.type !== 'test-record-snapshot' ||
              frame.v !== 'mol-test-run/v2' ||
              typeof frame.id !== 'string' ||
              typeof frame.chunk !== 'string' ||
              !Number.isSafeInteger(frame.count) ||
              !Number.isSafeInteger(frame.index) ||
              frame.count! <= 0 ||
              frame.count! > 128000 ||
              frame.index! < 0 ||
              frame.index! >= frame.count!
            )
              throw new Error('Invalid test feed snapshot')
            if (!assembly || assembly.id !== frame.id)
              assembly = { id: frame.id, count: frame.count!, chunks: [], bytes: 0 }
            if (assembly.count !== frame.count || frame.index !== assembly.chunks.length)
              throw new Error('Incomplete test feed snapshot')
            assembly.chunks.push(frame.chunk)
            assembly.bytes += frame.chunk.length
            if (assembly.bytes > 768 * 1024 * 1024) throw new Error('Test feed snapshot too large')
            if (assembly.chunks.length === assembly.count) {
              const payload = JSON.parse(assembly.chunks.join('')) as ProjectTestRecordPayload
              if (
                payload.snapshot?.v !== 'mol-test-run/v2' ||
                JSON.stringify(payload.snapshot.watermark) !==
                  JSON.stringify(payload.proof?.watermark)
              )
                throw new Error('Test/proof watermark mismatch')
              options.onSnapshot(payload)
              lastId = assembly.id
              assembly = null
            }
          }
        }
        if (!stopped) options.onUnavailable('The test feed is reconnecting.')
      } catch (_error) {
        if (!stopped) options.onUnavailable('The test feed is reconnecting.')
      }
      if (!stopped)
        await new Promise<void>((resolve) => {
          const finish = (): void => {
            clearTimeout(timer)
            controller.signal.removeEventListener('abort', finish)
            resolve()
          }
          const timer = setTimeout(finish, options.retryDelayMs ?? 1000)
          controller.signal.addEventListener('abort', finish, { once: true })
        })
    }
  }
  void loop()
  return () => {
    stopped = true
    controller.abort()
  }
}
export async function controlProjectTestRecord(options: {
  projectId: string
  runId: string
  plan: { id: string; stepId: string; generation: number }
  action: 'stop' | 'skip'
  requestId: string
  authToken?: string | null
  baseUrl?: string
  fetchImpl?: typeof fetch
}): Promise<{ accepted: boolean; state: string }> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (options.authToken) headers.Authorization = `Bearer ${options.authToken}`
  const response = await (options.fetchImpl || fetch)(
    `${options.baseUrl ?? API_BASE_URL}/projects/${options.projectId}/tests/run/${options.runId}/${options.action}`,
    {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify({
        planId: options.plan.id,
        stepId: options.plan.stepId,
        expectedGeneration: options.plan.generation,
        requestId: options.requestId,
      }),
    },
  )
  const body = (await response.json()) as { state?: string }
  return { accepted: response.ok, state: body.state || 'unavailable' }
}
